import { Draggable, DraggableProvider, reorderItems } from '@components/draggable';
import { IcoGripperHorizontal, IcoPlus, IcoTrash } from '@components/icons';
import { AutosizeText } from '@components/autosize-text';
import { Toggle } from '@components/toggle';
import { Button } from '@components/buttons';
import { FormQuestionAnswer } from './form-question-answer';
import { RuzcalFormQuestion } from './types';
import { generateUUID } from 'shared/utils';

interface Props {
  question: RuzcalFormQuestion;
  onChange: (updates: RuzcalFormQuestion) => void;
  onDelete: () => void;
}

export function FormQuestion({ question, onChange, onDelete }: Props) {
  const { answers } = question;
  const activeAnswers = answers.filter((c) => !c.deleted);

  function addNewAnswer() {
    onChange({
      ...question,
      answers: [
        ...answers,
        {
          id: generateUUID(),
          questionId: question.id,
          title: '',
          seq: answers.length,
          deleted: false,
        },
      ],
    });
  }

  return (
    <div class="border p-1 rounded-md mb-4 px-6 shadow-md">
      <div class="absolute w-full flex justify-center text-gray-400 cursor-move" draggable>
        <IcoGripperHorizontal class="w-5 h-5" />
      </div>

      <div class="mt-6 mb-4">
        <div class="relative">
          <AutosizeText
            class="ruz-input p-2 pr-8"
            minHeight=""
            placeholder="Write a question..."
            maxLength={4096}
            value={question.title || ''}
            focusSelf={true}
            onInput={(e: any) =>
              onChange({
                ...question,
                title: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div class="-ml-4">
        <DraggableProvider
          canHandleDrop={(_, table) => table === `question_${question.id}_choices`}
          onDragComplete={() => {}}
          onTargetChange={(dragState) =>
            onChange({
              ...question,
              answers: reorderItems(answers, dragState),
            })
          }
        >
          {activeAnswers.map((answer) => {
            return (
              <Draggable key={answer.id} id={answer.id} table={`question_${question.id}_choices`}>
                <FormQuestionAnswer
                  answer={answer}
                  draggable={true}
                  type={question.type}
                  onChange={(updates) => {
                    const newChoices = answers.map((x) =>
                      x.id === answer.id ? { ...x, ...updates } : x,
                    );

                    onChange({
                      ...question,
                      answers: newChoices,
                    });
                  }}
                ></FormQuestionAnswer>
              </Draggable>
            );
          })}
        </DraggableProvider>

        <div class="ml-4">
          <Button
            class="inline-flex items-center text-indigo-600 outline-hidden focus:ring-2 focus:ring-indigo-400 rounded-sm"
            onClick={addNewAnswer}
          >
            <IcoPlus class="h-6 w-6 mr-1 opacity-75" />
            Add a new option
          </Button>
        </div>
      </div>

      <footer class="flex justify-between border-t mt-4 pt-4 pb-2">
        <div class="flex items-center">
          <label>
            <select
              class="inline-ruz-input max-w-fit cursor-pointer"
              name="form-type"
              value={question.type}
              onInput={(e: any) => {
                onChange({ ...question, type: e.target.value });
              }}
            >
              <option value="checkbox">Multiple choices</option>
              <option value="radio">Single choice</option>
              <option value="text">Short Text</option>
            </select>
          </label>
        </div>
        <div class="flex items-center space-x-6">
          <div class="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-6">
            <label>
              <Toggle
                checked={question.isRequired}
                onClick={() => {
                  onChange({
                    ...question,
                    isRequired: !question.isRequired,
                  });
                }}
              />
              <span class="ml-2 text-xs">Required</span>
            </label>
          </div>

          <div class="pl-4">
            <Button
              class="p-2 -mr-2 text-gray-500 hover:bg-red-500 hover:text-white focus:ring-2 focus:ring-red-500 outline-hidden rounded-sm"
              onClick={onDelete}
              title="Delete"
            >
              <IcoTrash />
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
